import { useStyletron } from '@thanx/uikit/theme'
import React from 'react'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export interface InlineSelectTriggerProps
  extends React.HTMLAttributes<HTMLSpanElement> {
  /** Whether the dropdown is currently open */
  isOpen?: boolean
  /** Whether the trigger is disabled */
  disabled?: boolean
  /** Called when the trigger is clicked */
  onToggle?: () => void
  /** Content to display (selected value label) */
  children?: React.ReactNode
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Controlled InlineSelectTrigger component.
 *
 * The clickable trigger area of an InlineSelect. Shows current selection.
 * Styled with dashed underline and minimal chrome to blend with text.
 * All visual state determined by props. Spreads ...props for HOC injection.
 */
const InlineSelectTrigger = React.memo(
  ({
    isOpen = false,
    disabled = false,
    onToggle,
    children,
    style,
    onClick,
    className,
    ...props
  }: InlineSelectTriggerProps) => {
    const [css, theme] = useStyletron()

    const handleClick = (e: React.MouseEvent<HTMLSpanElement>) => {
      if (disabled) return
      onToggle?.()
      onClick?.(e)
    }

    const triggerClassName = css(getTriggerStyles(theme, { isOpen, disabled }))

    return (
      <span
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={isOpen}
        aria-disabled={disabled}
        onClick={handleClick}
        className={`${triggerClassName} ${className || ''}`}
        style={style}
        {...props}
      >
        {children}
      </span>
    )
  }
)

// ═══════════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════════

interface StyleParams {
  isOpen: boolean
  disabled: boolean
}

const CHEVRON_DOWN_SVG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`

const CHEVRON_UP_SVG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' style='transform:rotate(180deg)'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`

const getTriggerStyles = (theme: any, { isOpen, disabled }: StyleParams) => {
  const getBackgroundImage = () => {
    if (disabled) return 'none'
    if (isOpen) return CHEVRON_UP_SVG
    return CHEVRON_DOWN_SVG
  }

  return {
    display: 'inline',
    border: 'none',
    borderBottom: disabled ? 'none' : `1px dashed ${theme.colors.grey60}`,
    background: 'transparent',
    color: theme.colors.grey70,
    fontWeight: 500,
    fontSize: 'inherit',
    fontFamily: 'inherit',
    padding: disabled ? '0 4px 0 4px' : '0 18px 0 4px',
    cursor: disabled ? 'default' : 'pointer',
    backgroundImage: getBackgroundImage(),
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right center',
  }
}

export default InlineSelectTrigger
